import { type TFunction } from 'i18next';

import { type ServerAiBuilderChatErrorCode } from './serverAiBuilderChatDtos';

type AiBuilderSourceEntry = 'file' | 'youtube' | 'website';

type SourceEntryPrompts = {
  createFromSource: string;
  extractKeyConcepts: string;
  createEasyFromSource: string;
  addFromSource: string;
};

export type AiBuilderChatCopy = ReturnType<typeof createAiBuilderChatCopy>;

const tr = (
  t: TFunction<'build'>,
  key: string,
  options?: Record<string, unknown>,
): string => {
  const buildT = t as unknown as (
    key: string,
    options?: Record<string, unknown>,
  ) => unknown;

  return String(buildT(key, options) ?? '');
};

const normalizeDefaultPlaceholder = (placeholder: string) => {
  if (placeholder === 'AI에게 무엇이든 요청해 보세요') {
    return 'AI에게 원하는 퀴즈를 말해 주세요';
  }

  return placeholder;
};

// Central copy catalog intentionally stays as one typed object so callers share
// a single locale snapshot; semantic server copy adds no runtime abstraction.
// eslint-disable-next-line max-lines-per-function
export const createAiBuilderChatCopy = (t: TFunction<'build'>) => ({
  placeholder: {
    default: normalizeDefaultPlaceholder(
      tr(t, 'build:aiBuilderChat.placeholder.default'),
    ),
    softChoice: tr(t, 'build:aiBuilderChat.placeholder.softChoice'),
    closedAction: tr(t, 'build:aiBuilderChat.placeholder.closedAction'),
    file: tr(t, 'build:aiBuilderChat.placeholder.file'),
    youtube: tr(t, 'build:aiBuilderChat.placeholder.youtube'),
    website: tr(t, 'build:aiBuilderChat.placeholder.website'),
  },
  toast: {
    sourceDraftConflict: tr(t, 'build:aiBuilderChat.toast.sourceDraftConflict'),
  },
  usage: {
    blocked: tr(t, 'build:aiBuilderChat.usage.blocked'),
    remainingTurns: (remaining: number) =>
      tr(t, 'build:aiBuilderChat.usage.remainingTurns', { remaining }),
  },
  action: {
    undoLastAiAction: tr(t, 'build:aiBuilderChat.action.undoLastAiAction'),
    complete: tr(t, 'build:aiBuilderChat.action.complete'),
    defaultApprove: tr(t, 'build:aiBuilderChat.action.defaultApprove'),
    defaultCancel: tr(t, 'build:aiBuilderChat.action.defaultCancel'),
  },
  composer: {
    disclaimer: tr(t, 'build:aiBuilderChat.composer.disclaimer'),
    learnMore: tr(t, 'build:aiBuilderChat.composer.learnMore'),
    learnMoreUrl: tr(t, 'build:aiBuilderChat.composer.learnMoreUrl'),
    addSourceAriaLabel: tr(
      t,
      'build:aiBuilderChat.composer.addSourceAriaLabel',
    ),
    cancelGenerationAriaLabel: tr(
      t,
      'build:aiBuilderChat.composer.cancelGenerationAriaLabel',
    ),
    sendAriaLabel: tr(t, 'build:aiBuilderChat.composer.sendAriaLabel'),
    sourceInputTooltip: tr(
      t,
      'build:aiBuilderChat.composer.sourceInputTooltip',
    ),
  },
  panel: {
    title: tr(t, 'build:aiBuilderChat.panel.title'),
    optionsAriaLabel: tr(t, 'build:aiBuilderChat.panel.optionsAriaLabel'),
    restart: tr(t, 'build:aiBuilderChat.panel.restart'),
    close: tr(t, 'build:aiBuilderChat.panel.close'),
  },
  closeConfirm: {
    title: tr(t, 'build:aiBuilderChat.closeConfirm.title'),
    description: tr(t, 'build:aiBuilderChat.closeConfirm.description'),
    cancel: tr(t, 'build:aiBuilderChat.closeConfirm.cancel'),
    close: tr(t, 'build:aiBuilderChat.closeConfirm.close'),
  },
  copyButton: {
    ariaLabel: tr(t, 'build:aiBuilderChat.copyButton.ariaLabel'),
    tooltip: tr(t, 'build:aiBuilderChat.copyButton.tooltip'),
  },
  generationOverlay: {
    applying: tr(t, 'build:aiBuilderChat.generationOverlay.applying'),
    generating: tr(t, 'build:aiBuilderChat.generationOverlay.generating'),
    cancel: tr(t, 'build:aiBuilderChat.generationOverlay.cancel'),
  },
  jumpLinks: {
    moreAriaLabel: tr(t, 'build:aiBuilderChat.jumpLinks.moreAriaLabel'),
    more: tr(t, 'build:aiBuilderChat.jumpLinks.more'),
    jumpToQuestion: (label: string) =>
      tr(t, 'build:aiBuilderChat.jumpLinks.jumpToQuestion', { label }),
  },
  selectedQuizContext: {
    clearAriaLabel: (number: number) =>
      tr(t, 'build:aiBuilderChat.selectedQuizContext.clearAriaLabel', {
        number,
      }),
    label: (number: number) =>
      tr(t, 'build:aiBuilderChat.selectedQuizContext.label', { number }),
  },
  selectedChoice: {
    clearAriaLabel: (label: string) =>
      tr(t, 'build:aiBuilderChat.selectedChoice.clearAriaLabel', { label }),
  },
  fileChip: {
    removeAriaLabel: tr(t, 'build:aiBuilderChat.fileChip.removeAriaLabel'),
  },
  choice: {
    selectedAriaLabel: tr(t, 'build:aiBuilderChat.choice.selectedAriaLabel'),
    singleSelectionAriaLabel: tr(
      t,
      'build:aiBuilderChat.choice.singleSelectionAriaLabel',
    ),
    multipleSelectionAriaLabel: tr(
      t,
      'build:aiBuilderChat.choice.multipleSelectionAriaLabel',
    ),
    // 서버 missing_slot='quiz_types' 질문의 canonical id → 표시 라벨
    // (label→value 계약 — 서버는 값만 보내고 라벨은 FE locale 카피).
    // 빌더 유형 버튼과 같은 기존 번역 키를 재사용한다 — 신규 시트 등록 불필요.
    quizTypeOptionLabels: {
      ox: tr(t, 'build:build.ox.button'),
      multiple: tr(t, 'build:build.multiple.button'),
      subjective: tr(t, 'build:build.subjective.button'),
      essay: tr(t, 'build:build.quizType.essayQuestion'),
    } as Record<string, string | undefined>,
    // 서버 missing_slot='worksheet_import' 질문의 canonical id → 표시 라벨.
    // quiz_types 와 같은 계약 — 서버는 id 만 보내고 라벨은 FE locale 카피.
    worksheetImportOptionLabels: {
      import_source: tr(
        t,
        'build:aiBuilderChat.serverSemantic.question.worksheetImportOption.importSource',
      ),
      generate_new: tr(
        t,
        'build:aiBuilderChat.serverSemantic.question.worksheetImportOption.generateNew',
      ),
    } as Record<string, string | undefined>,
    countOption: (count: number) =>
      tr(t, 'build:aiBuilderChat.serverSemantic.question.countOption', {
        count,
      }),
  },
  quizCardChoice: {
    title: tr(t, 'build:aiBuilderChat.quizCardChoice.title'),
    refineOption: tr(t, 'build:aiBuilderChat.quizCardChoice.refineOption'),
    transformOption: tr(
      t,
      'build:aiBuilderChat.quizCardChoice.transformOption',
    ),
    similarOption: tr(t, 'build:aiBuilderChat.quizCardChoice.similarOption'),
  },
  userMessage: {
    sending: tr(t, 'build:aiBuilderChat.userMessage.sending'),
    failed: tr(t, 'build:aiBuilderChat.userMessage.failed'),
    retry: tr(t, 'build:aiBuilderChat.userMessage.retry'),
  },
  intro: {
    greetingWithName: (name: string) =>
      tr(t, 'build:aiBuilderChat.intro.greetingWithName', { name }),
    greetingWithoutName: tr(t, 'build:aiBuilderChat.intro.greetingWithoutName'),
    hasQuestionsFullLead: tr(
      t,
      'build:aiBuilderChat.intro.hasQuestionsFullLead',
    ),
    hasQuestionsNotFullLead: tr(
      t,
      'build:aiBuilderChat.intro.hasQuestionsNotFullLead',
    ),
    newEmptyLead: tr(t, 'build:aiBuilderChat.intro.newEmptyLead'),
    scopedNewEmptyLead: (scopeLabel: string) =>
      tr(t, 'build:aiBuilderChat.intro.scopedNewEmptyLead', { scopeLabel }),
    fileGuide: tr(t, 'build:aiBuilderChat.intro.fileGuide'),
    youtubeGuide: tr(t, 'build:aiBuilderChat.intro.youtubeGuide'),
    websiteGuide: tr(t, 'build:aiBuilderChat.intro.websiteGuide'),
    chatGuide: tr(t, 'build:aiBuilderChat.intro.chatGuide'),
  },
  welcome: {
    addMoreQuestions: tr(t, 'build:aiBuilderChat.welcome.addMoreQuestions'),
    createTenQuestions: tr(t, 'build:aiBuilderChat.welcome.createTenQuestions'),
    createTenScopedQuestions: (scopeLabel: string) =>
      tr(t, 'build:aiBuilderChat.welcome.createTenScopedQuestions', {
        scopeLabel,
      }),
    createMixedQuestions: tr(
      t,
      'build:aiBuilderChat.welcome.createMixedQuestions',
    ),
    createMixedScopedQuestions: (scopeLabel: string) =>
      tr(t, 'build:aiBuilderChat.welcome.createMixedScopedQuestions', {
        scopeLabel,
      }),
    reviewAllQuestions: tr(t, 'build:aiBuilderChat.welcome.reviewAllQuestions'),
    polishQuestionAndOptions: tr(
      t,
      'build:aiBuilderChat.welcome.polishQuestionAndOptions',
    ),
    checkDifficultyBalance: tr(
      t,
      'build:aiBuilderChat.welcome.checkDifficultyBalance',
    ),
    createEasyForGrade: ({
      grade,
      subjectPart,
    }: {
      grade: string;
      subjectPart: string;
    }) =>
      tr(t, 'build:aiBuilderChat.welcome.createEasyForGrade', {
        grade,
        subjectPart,
      }),
    createEasyForSubject: (subject: string) =>
      tr(t, 'build:aiBuilderChat.welcome.createEasyForSubject', { subject }),
    createEasyQuestions: tr(
      t,
      'build:aiBuilderChat.welcome.createEasyQuestions',
    ),
    sourcePrompts: {
      file: {
        createFromSource: tr(
          t,
          'build:aiBuilderChat.welcome.source.file.createFromSource',
        ),
        extractKeyConcepts: tr(
          t,
          'build:aiBuilderChat.welcome.source.file.extractKeyConcepts',
        ),
        createEasyFromSource: tr(
          t,
          'build:aiBuilderChat.welcome.source.file.createEasyFromSource',
        ),
        addFromSource: tr(
          t,
          'build:aiBuilderChat.welcome.source.file.addFromSource',
        ),
      },
      youtube: {
        createFromSource: tr(
          t,
          'build:aiBuilderChat.welcome.source.youtube.createFromSource',
        ),
        extractKeyConcepts: tr(
          t,
          'build:aiBuilderChat.welcome.source.youtube.extractKeyConcepts',
        ),
        createEasyFromSource: tr(
          t,
          'build:aiBuilderChat.welcome.source.youtube.createEasyFromSource',
        ),
        addFromSource: tr(
          t,
          'build:aiBuilderChat.welcome.source.youtube.addFromSource',
        ),
      },
      website: {
        createFromSource: tr(
          t,
          'build:aiBuilderChat.welcome.source.website.createFromSource',
        ),
        extractKeyConcepts: tr(
          t,
          'build:aiBuilderChat.welcome.source.website.extractKeyConcepts',
        ),
        createEasyFromSource: tr(
          t,
          'build:aiBuilderChat.welcome.source.website.createEasyFromSource',
        ),
        addFromSource: tr(
          t,
          'build:aiBuilderChat.welcome.source.website.addFromSource',
        ),
      },
    } satisfies Record<AiBuilderSourceEntry, SourceEntryPrompts>,
  },
  system: {
    requestCanceled: tr(t, 'build:aiBuilderChat.system.requestCanceled'),
    progressStopped: tr(t, 'build:aiBuilderChat.system.progressStopped'),
    runRejectedProgressTitle: tr(
      t,
      'build:aiBuilderChat.system.runRejectedProgressTitle',
    ),
    temporaryError: tr(t, 'build:aiBuilderChat.system.temporaryError'),
    resetSessionFailed: tr(t, 'build:aiBuilderChat.system.resetSessionFailed'),
    sourceRegistrationUnavailable: tr(
      t,
      'build:aiBuilderChat.system.sourceRegistrationUnavailable',
    ),
    sourceRegistrationSessionRequired: tr(
      t,
      'build:aiBuilderChat.system.sourceRegistrationSessionRequired',
    ),
    sourceRegistrationFailed: tr(
      t,
      'build:aiBuilderChat.system.sourceRegistrationFailed',
    ),
    defaultFilePrompt: tr(t, 'build:aiBuilderChat.system.defaultFilePrompt'),
    submitFailed: tr(t, 'build:aiBuilderChat.system.submitFailed'),
    retryFailed: tr(t, 'build:aiBuilderChat.system.retryFailed'),
    staleQuestionList: tr(t, 'build:aiBuilderChat.system.staleQuestionList'),
    questionLimitAppendFailed: tr(
      t,
      'build:aiBuilderChat.system.questionLimitAppendFailed',
    ),
    questionLimitApplyFailed: tr(
      t,
      'build:aiBuilderChat.system.questionLimitApplyFailed',
    ),
    undoUnavailable: tr(t, 'build:aiBuilderChat.system.undoUnavailable'),
    undoComplete: tr(t, 'build:aiBuilderChat.system.undoComplete'),
  },
  sourcePreparation: {
    runningTitle: tr(t, 'build:aiBuilderChat.sourcePreparation.runningTitle'),
    readSourceRunning: tr(
      t,
      'build:aiBuilderChat.sourcePreparation.readSourceRunning',
    ),
    prepareGenerationPending: tr(
      t,
      'build:aiBuilderChat.sourcePreparation.prepareGenerationPending',
    ),
    failedTitle: tr(t, 'build:aiBuilderChat.sourcePreparation.failedTitle'),
    readSourceFailed: tr(
      t,
      'build:aiBuilderChat.sourcePreparation.readSourceFailed',
    ),
    prepareGenerationFailed: tr(
      t,
      'build:aiBuilderChat.sourcePreparation.prepareGenerationFailed',
    ),
  },
  serverEvent: {
    pendingProgressTitle: tr(
      t,
      'build:aiBuilderChat.serverEvent.pendingProgressTitle',
    ),
    createdQuestionsProgressTitle: (count: number) =>
      tr(t, 'build:aiBuilderChat.serverEvent.createdQuestionsProgressTitle', {
        count,
      }),
    questionGenerationComplete: tr(
      t,
      'build:aiBuilderChat.serverEvent.questionGenerationComplete',
    ),
    changesApplied: tr(t, 'build:aiBuilderChat.serverEvent.changesApplied'),
    taskComplete: tr(t, 'build:aiBuilderChat.serverEvent.taskComplete'),
    questionLabel: (number: number) =>
      tr(t, 'build:aiBuilderChat.serverEvent.questionLabel', { number }),
  },
  // 서버 progress 이벤트의 message_code → 카피 (RFC #412 code 계약).
  // 미등록 code 는 서버 message verbatim 폴백.
  serverProgress: {
    byCode: {
      PROGRESS_GENERATING: tr(
        t,
        'build:aiBuilderChat.serverProgress.generating',
      ),
      PROGRESS_SEARCHING: tr(t, 'build:aiBuilderChat.serverProgress.searching'),
      PROGRESS_EXTRACTING: tr(
        t,
        'build:aiBuilderChat.serverProgress.extracting',
      ),
      PROGRESS_READING: tr(t, 'build:aiBuilderChat.serverProgress.reading'),
      PROGRESS_APPLYING: tr(t, 'build:aiBuilderChat.serverProgress.applying'),
      PROGRESS_CHECKING_REQUEST: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingRequest',
      ),
      PROGRESS_CHECKING_SOURCE: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingSource',
      ),
      PROGRESS_GENERATE_PREPARING_INPUT: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingRequest',
      ),
      PROGRESS_GENERATE_APPLYING_CONTEXT: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingSource',
      ),
      PROGRESS_GENERATE_DRAFTING_QUESTIONS: tr(
        t,
        'build:aiBuilderChat.serverProgress.generating',
      ),
      PROGRESS_GENERATE_VALIDATING_QUESTIONS: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingRequest',
      ),
      PROGRESS_GENERATE_RETRYING: tr(
        t,
        'build:aiBuilderChat.serverProgress.generating',
      ),
      PROGRESS_GENERATE_NORMALIZING_QUESTIONS: tr(
        t,
        'build:aiBuilderChat.serverProgress.applying',
      ),
      PROGRESS_EDIT_REVIEWING_QUESTIONS: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingRequest',
      ),
      PROGRESS_EDIT_REVISING_QUESTIONS: tr(
        t,
        'build:aiBuilderChat.serverProgress.applying',
      ),
      PROGRESS_EDIT_VALIDATING_CHANGES: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingRequest',
      ),
      PROGRESS_IMPORT_READING_SOURCE: tr(
        t,
        'build:aiBuilderChat.serverProgress.extracting',
      ),
      PROGRESS_YOUTUBE_VALIDATING_URL: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingSource',
      ),
      PROGRESS_YOUTUBE_EXTRACTING_CAPTIONS: tr(
        t,
        'build:aiBuilderChat.serverProgress.extracting',
      ),
      PROGRESS_YOUTUBE_REGISTERING_SOURCE: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingSource',
      ),
      PROGRESS_WEBSITE_VALIDATING_URL: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingSource',
      ),
      PROGRESS_WEBSITE_FETCHING_CONTENT: tr(
        t,
        'build:aiBuilderChat.serverProgress.extracting',
      ),
      PROGRESS_WEBSITE_REGISTERING_SOURCE: tr(
        t,
        'build:aiBuilderChat.serverProgress.checkingSource',
      ),
    } satisfies Record<string, string>,
  },
  // 서버 confirm_required 의 code+params → 카피 (RFC #412 code 계약).
  // code 미등록/params 불충분이면 서버 message/label verbatim 폴백.
  serverConfirm: {
    deleteSingleQuestion: (number: number) =>
      tr(t, 'build:aiBuilderChat.serverConfirm.deleteSingleQuestion', {
        number,
      }),
    deleteQuestions: (count: number) =>
      tr(t, 'build:aiBuilderChat.serverConfirm.deleteQuestions', { count }),
    deleteApprove: tr(t, 'build:aiBuilderChat.serverConfirm.deleteApprove'),
    reduceCount: (count: number) =>
      tr(t, 'build:aiBuilderChat.serverConfirm.reduceCount', { count }),
    reduceApprove: tr(t, 'build:aiBuilderChat.serverConfirm.reduceApprove'),
    replaceQuestions: (count: number) =>
      tr(t, 'build:aiBuilderChat.serverConfirm.replaceQuestions', { count }),
    replaceApprove: tr(t, 'build:aiBuilderChat.serverConfirm.replaceApprove'),
  },
  serverAssistant: {
    terminalCompleted: tr(
      t,
      'build:aiBuilderChat.serverAssistant.terminalCompleted',
    ),
    terminalClarify: tr(
      t,
      'build:aiBuilderChat.serverAssistant.terminalClarify',
    ),
    terminalUndoCompleted: tr(
      t,
      'build:aiBuilderChat.serverAssistant.terminalUndoCompleted',
    ),
    terminalConfirmCancelled: tr(
      t,
      'build:aiBuilderChat.serverAssistant.terminalConfirmCancelled',
    ),
    terminalConfirmStale: tr(
      t,
      'build:aiBuilderChat.serverAssistant.terminalConfirmStale',
    ),
    questionLimitAdjusted: ({
      currentCount,
      adjustedCount,
      limit,
    }: {
      currentCount: number;
      adjustedCount: number;
      limit: number;
    }) =>
      tr(t, 'build:aiBuilderChat.serverAssistant.questionLimitAdjusted', {
        currentCount,
        adjustedCount,
        limit,
      }),
    questionLimitReached: ({
      currentCount,
      limit,
    }: {
      currentCount: number;
      limit: number;
    }) =>
      tr(t, 'build:aiBuilderChat.serverAssistant.questionLimitReached', {
        currentCount,
        limit,
      }),
    targetOutOfRangeReorder: ({
      numbers,
      totalCount,
    }: {
      numbers: string;
      totalCount: number;
    }) =>
      tr(t, 'build:aiBuilderChat.serverAssistant.targetOutOfRangeReorder', {
        numbers,
        totalCount,
      }),
  },
  serverSemantic: {
    targetRequiredReorder: tr(
      t,
      'build:aiBuilderChat.serverSemantic.targetRequiredReorder',
    ),
    source: {
      youtubeInvalidUrl: tr(
        t,
        'build:aiBuilderChat.serverError.reason.youtubeUrlInvalid',
      ),
      youtubeUnavailable: tr(
        t,
        'build:aiBuilderChat.serverError.reason.youtubeExtractFailed',
      ),
      youtubeCaptionsUnavailable: tr(
        t,
        'build:aiBuilderChat.serverError.reason.youtubeTranscriptEmpty',
      ),
      websiteInvalidUrl: tr(t, 'build:build.website.alert.invalidUrl.title'),
      websiteUnreadable: tr(
        t,
        'build:aiBuilderChat.serverError.reason.websiteUnreadable',
      ),
      websiteTooLarge: tr(
        t,
        'build:aiBuilderChat.serverError.reason.websiteTooLarge',
      ),
      checkFailed: tr(t, 'build:aiBuilderChat.serverError.fallback'),
    },
    completion: {
      generated: (createdCount: number) =>
        tr(t, 'build:aiBuilderChat.serverSemantic.completion.generated', {
          createdCount,
        }),
      imported: (importedCount: number) =>
        tr(t, 'build:aiBuilderChat.serverSemantic.completion.imported', {
          importedCount,
        }),
      importedPartial: (importedCount: number, skippedCount: number) =>
        tr(t, 'build:aiBuilderChat.serverSemantic.completion.importedPartial', {
          importedCount,
          skippedCount,
        }),
      importEmpty: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.importEmpty',
      ),
      regenerated: (changedCount: number) =>
        tr(t, 'build:aiBuilderChat.serverSemantic.completion.regenerated', {
          changedCount,
        }),
      replaced: (changedCount: number) =>
        tr(t, 'build:aiBuilderChat.serverSemantic.completion.replaced', {
          changedCount,
        }),
      hintsAdded: (changedCount: number) =>
        tr(t, 'build:aiBuilderChat.serverSemantic.completion.hintsAdded', {
          changedCount,
        }),
      edited: (changedCount: number) =>
        tr(t, 'build:aiBuilderChat.serverSemantic.completion.edited', {
          changedCount,
        }),
      editedPartial: (changedCount: number, failedCount: number) =>
        tr(t, 'build:aiBuilderChat.serverSemantic.completion.editedPartial', {
          changedCount,
          failedCount,
        }),
      reordered: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.reordered',
      ),
      noQuestions: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.noQuestions',
      ),
      questionNotFound: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.questionNotFound',
      ),
      noChanges: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.noChanges',
      ),
      invalidOrder: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.invalidOrder',
      ),
      notEnoughQuestions: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.notEnoughQuestions',
      ),
      editFailed: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.editFailed',
      ),
      regenerationFailed: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.regenerationFailed',
      ),
      hintFailed: tr(
        t,
        'build:aiBuilderChat.serverSemantic.completion.hintFailed',
      ),
    },
    action: {
      sourceMismatchProceed: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.sourceMismatchProceed.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.sourceMismatchProceed.prompt',
        ),
      },
      sourceMismatchStop: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.sourceMismatchStop.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.sourceMismatchStop.prompt',
        ),
      },
      reviewAnswers: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.reviewAnswers.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.reviewAnswers.prompt',
        ),
      },
      reviewSourceAlignment: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.reviewSourceAlignment.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.reviewSourceAlignment.prompt',
        ),
      },
      makeEasier: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.makeEasier.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.makeEasier.prompt',
        ),
      },
      makeHarder: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.makeHarder.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.makeHarder.prompt',
        ),
      },
      addSimilarQuestions: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.addSimilarQuestions.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.addSimilarQuestions.prompt',
        ),
      },
      mixQuestionTypes: {
        label: tr(t, 'build:aiBuilderChat.welcome.createMixedQuestions'),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.mixQuestionTypes.prompt',
        ),
      },
      addHints: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.addHints.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.addHints.prompt',
        ),
      },
      simplifyWording: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.simplifyWording.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.simplifyWording.prompt',
        ),
      },
      alignToGrade: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.alignToGrade.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.alignToGrade.prompt',
        ),
      },
      checkDuplicates: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.checkDuplicates.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.checkDuplicates.prompt',
        ),
      },
      fixProblematicParts: {
        label: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.fixProblematicParts.label',
        ),
        prompt: tr(
          t,
          'build:aiBuilderChat.serverSemantic.action.fixProblematicParts.prompt',
        ),
      },
    },
  },
  serverError: {
    fallback: tr(t, 'build:aiBuilderChat.serverError.fallback'),
    byCode: {
      USAGE_BLOCKED: tr(t, 'build:aiBuilderChat.usage.blocked'),
      USAGE_UNAVAILABLE: tr(
        t,
        'build:aiBuilderChat.serverError.usageUnavailable',
      ),
      SESSION_LOCKED: tr(t, 'build:aiBuilderChat.serverError.sessionLocked'),
      SESSION_NOT_FOUND: tr(
        t,
        'build:aiBuilderChat.serverError.sessionNotFound',
      ),
      SESSION_BINDING_MISMATCH: tr(
        t,
        'build:aiBuilderChat.serverError.sessionBindingMismatch',
      ),
      MESSAGE_TOO_LONG: tr(t, 'build:aiBuilderChat.serverError.messageTooLong'),
      GENERATION_EMPTY_RESULT: tr(
        t,
        'build:aiBuilderChat.serverError.generationEmptyResult',
      ),
      QUESTION_LIMIT_EXCEEDED: tr(
        t,
        'build:aiBuilderChat.serverError.questionLimitExceeded',
      ),
      SOURCE_REF_UNAVAILABLE: tr(
        t,
        'build:aiBuilderChat.serverError.sourceRefUnavailable',
      ),
      UNDO_UNAVAILABLE: tr(
        t,
        'build:aiBuilderChat.serverError.undoUnavailable',
      ),
      CONFIRM_TOKEN_INVALID: tr(
        t,
        'build:aiBuilderChat.serverError.confirmTokenInvalid',
      ),
      INTERNAL_ERROR: tr(t, 'build:aiBuilderChat.serverError.internalError'),
      UNSUPPORTED_FORMAT: tr(
        t,
        'build:aiBuilderChat.serverError.unsupportedFormat',
      ),
      EXTRACTION_FAILED: tr(
        t,
        'build:aiBuilderChat.serverError.extractionFailed',
      ),
      FILE_TOO_LARGE: tr(t, 'build:aiBuilderChat.serverError.fileTooLarge'),
      LOCKED_FILE: tr(t, 'build:aiBuilderChat.serverError.lockedFile'),
      TOO_MANY_PAGES: tr(t, 'build:aiBuilderChat.serverError.tooManyPages'),
      STORAGE_UPLOAD_FAILED: tr(
        t,
        'build:aiBuilderChat.serverError.storageUploadFailed',
      ),
    } satisfies Partial<Record<ServerAiBuilderChatErrorCode, string>>,
    // detail.reason 서브코드 → 카피 (code 보다 우선, RFC #412).
    byReason: {
      SOURCE_TYPE_UNSUPPORTED: tr(
        t,
        'build:aiBuilderChat.serverError.unsupportedFormat',
      ),
      FILE_TYPE_UNSUPPORTED: tr(
        t,
        'build:aiBuilderChat.serverError.reason.fileTypeUnsupported',
      ),
      URL_EMPTY: tr(t, 'build:aiBuilderChat.serverError.reason.urlEmpty'),
      YOUTUBE_URL_INVALID: tr(
        t,
        'build:aiBuilderChat.serverError.reason.youtubeUrlInvalid',
      ),
      FILE_EMPTY: tr(t, 'build:aiBuilderChat.serverError.reason.fileEmpty'),
      FILE_EXTRACT_FAILED: tr(
        t,
        'build:aiBuilderChat.serverError.reason.fileExtractFailed',
      ),
      FILE_LOCKED: tr(t, 'build:aiBuilderChat.serverError.lockedFile'),
      FILE_TOO_MANY_PAGES: tr(
        t,
        'build:aiBuilderChat.serverError.tooManyPages',
      ),
      WEBSITE_FETCH_FAILED: tr(
        t,
        'build:aiBuilderChat.serverError.reason.websiteFetchFailed',
      ),
      WEBSITE_UNREADABLE: tr(
        t,
        'build:aiBuilderChat.serverError.reason.websiteUnreadable',
      ),
      WEBSITE_TOO_LARGE: tr(
        t,
        'build:aiBuilderChat.serverError.reason.websiteTooLarge',
      ),
      YOUTUBE_EXTRACT_FAILED: tr(
        t,
        'build:aiBuilderChat.serverError.reason.youtubeExtractFailed',
      ),
      YOUTUBE_TRANSCRIPT_EMPTY: tr(
        t,
        'build:aiBuilderChat.serverError.reason.youtubeTranscriptEmpty',
      ),
      FILE_UPLOAD_FAILED: tr(
        t,
        'build:aiBuilderChat.serverError.storageUploadFailed',
      ),
      SOURCE_BODY_STORE_FAILED: tr(
        t,
        'build:aiBuilderChat.serverError.storageUploadFailed',
      ),
    } satisfies Record<string, string>,
  },
  legacyGenerationComplete: {
    content: ({
      selectedLabels,
      quizCount,
    }: {
      selectedLabels: string[];
      quizCount: number;
    }) =>
      tr(t, 'build:aiBuilderChat.legacyGenerationComplete.content', {
        selectedLabels: selectedLabels.join(', '),
        quizCount,
      }),
  },
});
